import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { testApi, getTestToken } from "@/lib/testApi";

/**
 * Post-checkout landing pages.
 *   /checkout/success?session_id=...    — waits for webhook fulfillment
 *   /checkout/cancelled                 — user closed the Stripe page
 */

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("waiting");     // waiting | fulfilled | timeout
  const [purchase, setPurchase] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Payment received · ImpactVest";
    if (!getTestToken()) { navigate("/access"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await testApi.get("/checkout/balance");
        const hit = (r.data.purchases || []).find(
          (p) => p.stripe_checkout_session_id === sessionId && p.fulfillment_status === "fulfilled",
        );
        if (hit) { setPurchase(hit); setStatus("fulfilled"); return; }
      } catch { /* ignore */ }
      if (attempts >= 20) { setStatus("timeout"); return; }
      setTimeout(poll, 1500);
    };
    poll();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-iv-bg text-iv-fg flex items-center" data-testid="checkout-success-page">
      <main className="iv-container py-14 max-w-[720px] mx-auto text-center">
        {status === "waiting" && (
          <>
            <Loader2 className="animate-spin mx-auto mb-6" size={40} style={{ color: "var(--iv-green)" }} />
            <h1 className="iv-h1 mb-4">Confirming your payment…</h1>
            <p className="iv-body-lg">
              Stripe is confirming your payment. We&rsquo;re preparing your private workspace
              automatically. This usually takes a few seconds.
            </p>
          </>
        )}
        {status === "fulfilled" && purchase && (
          <>
            <CheckCircle2 className="mx-auto mb-6" size={44} style={{ color: "var(--iv-green)" }} />
            <h1 className="iv-h1 mb-4">Your workspace is ready.</h1>
            <p className="iv-body-lg mb-8">
              We&rsquo;ve credited <b>{purchase.credits_granted}</b> analysis credit{purchase.credits_granted > 1 ? "s" : ""} to your account and
              created your private {purchase.pathway} workspace. Next step: confirm the
              upload authorisation and start your first analysis.
            </p>
            <Link
              to={`/live/start?path=${purchase.pathway}`}
              data-testid="success-consent-cta"
              className="iv-btn iv-btn--primary inline-flex"
            >Continue to workspace →</Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <XCircle className="mx-auto mb-6" size={44} style={{ color: "var(--iv-fg-3)" }} />
            <h1 className="iv-h1 mb-4">Still processing…</h1>
            <p className="iv-body-lg mb-6">
              Your payment was received but the confirmation is taking longer than usual.
              Stripe will still complete fulfilment. You&rsquo;ll see your workspace appear here
              automatically, or contact <a href="mailto:office@impactvest.com" style={{ color: "var(--iv-green)", textDecoration: "underline" }}>office@impactvest.com</a> if it takes more than five minutes.
            </p>
          </>
        )}
      </main>
    </div>
  );
}

export function CheckoutCancelled() {
  useEffect(() => { document.title = "Checkout cancelled · ImpactVest"; }, []);
  return (
    <div className="min-h-screen bg-iv-bg text-iv-fg flex items-center" data-testid="checkout-cancelled-page">
      <main className="iv-container py-14 max-w-[720px] mx-auto text-center">
        <h1 className="iv-h1 mb-4">Payment cancelled.</h1>
        <p className="iv-body-lg mb-8">
          Your card was not charged. You can choose a different pack at any time.
          No workspace was created.
        </p>
        <div className="flex gap-4 justify-center">
          <Link to="/pricing/investor" className="iv-btn" data-testid="cancelled-back-investor">Investors pricing</Link>
          <Link to="/pricing/company" className="iv-btn" data-testid="cancelled-back-company">Companies pricing</Link>
        </div>
      </main>
    </div>
  );
}
